import {
	getOasisAssessmentValuesOptions,
	OasisAddendumStatusEnum,
	type OasisAddendumViewModel,
	type OasisAssessmentViewModel,
	type OasisTaskViewModel
} from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { Fragment } from 'react';
import { Badge } from '~/shared/components/Badge';
import { Button } from '~/shared/components/buttons/Button';
import { SmallSection } from '~/shared/section/SmallSection';
import { getOasisAnswerRows } from './oasisAnswerRows';
import { OfficeReviewOasisAddendumFormModal } from '~/features/oasis/addendums/OfficeReviewOasisAddendumFormModal';

export function OasisTabDetails({ oasisAssessment }: { oasisAssessment: OasisAssessmentViewModel }) {
	const { data } = useSuspenseQuery(getOasisAssessmentValuesOptions(oasisAssessment.id));
	const tasksBySection = data.oasisTasks.reduce((acc, task) => {
		const letter = task.oasisDefinition.oasisQuestionSectionLetter;
		(acc[letter] ??= []).push(task);
		return acc;
	}, {} as Record<string, OasisTaskViewModel[]>);

	const pendingAddendums = oasisAssessment.addendums.filter(x => x.oasisAddendumStatusId === OasisAddendumStatusEnum.NeedsResponse);

	return (
		<div className="flex flex-col gap-4">
			{oasisAssessment.oasisAssessmentDefinition.oasisAssessmentSections
				.filter(section => tasksBySection[section.letter]?.length > 0)
				.map(section => {
					const tasks = tasksBySection[section.letter] ?? [];
					return (
						<SmallSection
							key={section.letter}
							title={`${section.letter}. ${section.label}`}
							subtitle={`(${tasks.length} items)`}
							useExpanded
							expandedOnLoad={false}
						>
							<div className="divide-y divide-gray-100">
								{tasks.map(task => (
									<OasisTaskAnswers
										key={task.id}
										task={task}
										allTasks={data.oasisTasks}
										pendingAddendum={pendingAddendums.find(x => x.oasisTaskId === task.id)}
									/>
								))}
							</div>
						</SmallSection>
					);
				})}
		</div>
	);
}

function OasisTaskAnswers({
	task,
	allTasks,
	pendingAddendum
}: {
	task: OasisTaskViewModel;
	allTasks: OasisTaskViewModel[];
	pendingAddendum?: OasisAddendumViewModel;
}) {
	const rows = getOasisAnswerRows(task.oasisDefinition, task.values ?? {});
	const title = task.oasisDefinition.menuLabel ?? task.oasisDefinition.title;
	return (
		<div className="flex items-start gap-3 px-4 py-2.5">
			<span className="w-24 shrink-0 pt-0.5 font-mono text-[10px] font-semibold text-gray-400">{title}</span>
			<dl className="grid min-w-0 flex-1 grid-cols-[minmax(0,14rem)_1fr] gap-x-3 gap-y-1">
				{rows.map(row => (
					<Fragment key={row.code}>
						<dt className="truncate text-[11px] text-gray-500" title={`${row.code} — ${row.label}`}>
							{row.label === title ? row.code : row.label}
						</dt>
						<dd className="text-xs text-gray-800">
							{row.answerHtml ? (
								<span dangerouslySetInnerHTML={{ __html: row.answerHtml }} />
							) : (
								<span className="italic text-gray-300">Not assessed</span>
							)}
						</dd>
					</Fragment>
				))}
			</dl>
			<div className="shrink-0">
				{pendingAddendum ? (
					<Badge intent="warning">Addendum pending</Badge>
				) : (
					<Button
						label="Edit"
						Icon={Pencil}
						size="xs"
						appearance="outline"
						intent="neutral"
						renderModal={closeModal => (
							<OfficeReviewOasisAddendumFormModal oasisTask={task} allOasisTasks={allTasks} closeModal={closeModal} />
						)}
					/>
				)}
			</div>
		</div>
	);
}
